import { addDays, subDays } from 'date-fns';

import type { SubscriptionPeriodPayment } from '@models/subscriptions';
import { PaymentSearchByFields } from '@models/payments';
import type { GetPaymentsParams } from '@features/payments/api';
import { usePaymentsQuery } from '@features/payments/queries';
import { formatYmd } from '@utils/date';

import LoadingAnimation from '@components/LoadingAnimation';
import PaymentDetailDrawer from '@components/Payments/PaymentDetailDrawer';
import s from './SubscriptionDetailDrawer.module.css';

type SubscriptionPeriodPaymentDrawerProps = {
  payment: SubscriptionPeriodPayment;
  onClose: () => void;
};

// A period only carries a payment summary, while the payment drawer needs the full row — so fetch it by id,
// bounded to the day around its createdAt (the list endpoint filters on payment.createdAt, the partition key).
function SubscriptionPeriodPaymentDrawer({
  payment,
  onClose,
}: SubscriptionPeriodPaymentDrawerProps) {
  const createdAt = new Date(payment.createdAt);
  const queryParams: GetPaymentsParams = {
    page: 1,
    limit: 1,
    fromDate: formatYmd(subDays(createdAt, 1)),
    toDate: formatYmd(addDays(createdAt, 1)),
    searchQuery: payment.id,
    searchBy: PaymentSearchByFields.PAYMENT_ID,
  };

  const { data, isLoading } = usePaymentsQuery(queryParams);
  const fullPayment = data?.data[0] ?? null;

  if (!fullPayment) {
    return (
      <>
        <div className={s.paymentLoadingScrim} onClick={onClose} />
        <aside className={s.paymentLoadingDrawer} role="dialog" aria-label="Payment details">
          {isLoading ? (
            <LoadingAnimation compact />
          ) : (
            <p className={s.errorText}>Could not load this payment.</p>
          )}
        </aside>
      </>
    );
  }

  return <PaymentDetailDrawer payment={fullPayment} onClose={onClose} />;
}

export default SubscriptionPeriodPaymentDrawer;
